/**
 * Scope Reference Data Policy (Reglas tab)
 *
 * The load-bearing bridge between the rules data and the inheritance resolver.
 * On store init it:
 *   1. Fetches ALL account rules INCLUDING `kind: 'bypass'` rows (reusing the
 *      exact RULES_FILTER + RULES_SELECT from the shared rulesQueryPolicy so the
 *      bypass rows the `/rules` route default-excludes come back) and dispatches
 *      them to `data/setRules`.
 *   2. Fetches the connected sales-channel integrations (names + logos) from the
 *      `ExternalSystemsIntegrations` readmodel.
 *   3. Builds the SPARSE scope tree (Pattern A — SDD "Scope tree") and dispatches
 *      it to `data/setScopeTree`.
 *
 * CRITICAL — `ancestors` correctness: each `ScopeNode.ancestors` is the ordered
 * (root → self) hierarchy path the resolver replays to mirror the backend's
 * `findRulesToExecute` precedence walk. The walk is driven by
 * `HIERARCHY_PRECEDENCE` (defined once in `@bett3r-dev/teselly-domain`
 * rules-hierarchy.ts, shared by the backend and this resolver):
 *   publication: [ publication, channel, integrationUserId, account ]
 *   item:        [ item, warehouse, account ]
 *   order:       [ order, channel, integrationUserId, account ]
 * So:
 *   - account root : [ { account } ]                        (belongs to both)
 *   - channel node : [ { account }, { channel }, { integrationUserId } ]
 *                    (channel + integrationUserId BOTH carry the integration id —
 *                     back-compat per `scopeTypes.ts` / `rules-utils.ts`; including
 *                     both lets the resolver match channel rules stored at either
 *                     `hierarchyLevel`).
 *   - publication  : [ { account }, { channel }, { publication } ] (hierarchy
 *                    'publication'; channel step derived from the publication's
 *                    own integration so channel-level rules are inherited).
 *   - item         : [ { account }, { item } ]              (hierarchy 'item';
 *                    warehouse step omitted — rules data carries no item→warehouse
 *                    link; warehouse-level inheritance is a documented v1 gap).
 *
 * SPARSE LEAVES (Pattern A): publication / item leaves are ONLY the distinct
 * entities that OWN rules (appear in some rule's `hierarchyEntityIds` at the
 * corresponding `hierarchyLevel`), capped at the top 25 per group sorted by
 * own-rule-count desc. Names are enriched best-effort with a BOUNDED per-id
 * lookup over just those ≤25 ids (never the full 8k catalog).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import {
  RulesReadmodelType,
  SystemIntegrationsReadmodelType
} from '@bett3r-dev/teselly-domain';
import { ScopeAncestor, ScopeNode } from '../../../shared/utils/scopeTypes';
import { RulesCatalogState } from '../state';
import { RulesCatalogCommand } from '../commands';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Explicit `kind` clause: match both ordinary rules (`kind` absent) and bypass
 * rules. Replicated from `shared/state/policies/rulesQueryPolicy.ts` (#3) — the
 * `/rules` route silently drops bypass rows without it, which would make the
 * suppression calc in `resolveRulesForScope` show suppressed rules as active.
 */
const RULES_FILTER: NonNullable<
  Parameters<ClientLibraryType['queries']['Rules']>[0]
>['filter'] = [
  {
    OR: [
      { field: 'kind', op: 'eq', value: 'bypass' },
      { field: 'kind', op: 'notExists' }
    ]
  }
];

/**
 * Fields consumed by the catalog: the suppression / precedence walk in
 * `resolveRulesForScope` (`hierarchy`, `hierarchyLevel`, `hierarchyEntityIds`,
 * `overrideGroups`, `kind`, `targets`, `ruleDefinitionId`, `ruleId`, `enabled`)
 * plus `name` / `description` / `categories` for the table + scope-tree labels.
 * Mirrors the shared rulesQueryPolicy select.
 */
const RULES_SELECT: ( keyof RulesReadmodelType )[] = [
  'id',
  'name',
  'description',
  'ruleDefinition',
  'ruleDefinitionId',
  'ruleId',
  'kind',
  'targets',
  'enabled',
  'categories',
  'triggeringEvents',
  'declaredInputsValues',
  'hierarchy',
  'hierarchyLevel',
  'hierarchyEntityIds',
  'overrideGroups',
  'accountId'
];

/** Max sparse leaves materialized per group (Pattern A default). */
const MAX_LEAVES_PER_GROUP = 25;

/** Synthetic root node id. The `'all'` selection resolves the same set. */
const ACCOUNT_NODE_ID = 'account';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Resolve a multi-language or plain-string label to a display string, falling
 * back to the supplied id.
 */
const resolveLabel = ( raw: unknown, fallback: string ): string => {
  if ( typeof raw === 'string' && raw.trim().length > 0 ) return raw;
  if ( raw && typeof raw === 'object' ) {
    const rec = raw as Record<string, string>;
    return rec.es || rec.en || rec.pt || fallback;
  }
  return fallback;
};

/**
 * Derive the account id from the fetched rules. Account-level rules register
 * their account id both as `accountId` and as the entityId under
 * `hierarchyLevel: 'account'`. We prefer the explicit `accountId`, then the
 * account-level entityId, so the root node carries the correct
 * `{ level: 'account', entityId }` step the resolver matches account rules on.
 */
const deriveAccountId = ( rules: RulesReadmodelType[]): string => {
  const withAccountId = rules.find(( r ) => !!r.accountId );
  if ( withAccountId?.accountId ) return withAccountId.accountId;

  const accountRule = rules.find(
    ( r ) => r.hierarchyLevel === 'account' && ( r.hierarchyEntityIds || []).length > 0
  );
  if ( accountRule ) return accountRule.hierarchyEntityIds[0];

  return ACCOUNT_NODE_ID;
};

/**
 * Distinct entity ids that own rules at a given `hierarchyLevel`, sorted by
 * own-rule-count desc, capped at `MAX_LEAVES_PER_GROUP` (Pattern A).
 */
const topEntityIdsForLevel = (
  rules: RulesReadmodelType[],
  level: RulesReadmodelType['hierarchyLevel']
): { entityId: string; count: number }[] => {
  const counts = new Map<string, number>();
  rules.forEach(( rule ) => {
    if ( rule.kind === 'bypass' ) return;
    if ( rule.hierarchyLevel !== level ) return;
    ( rule.hierarchyEntityIds || []).forEach(( entityId ) => {
      counts.set( entityId, ( counts.get( entityId ) ?? 0 ) + 1 );
    });
  });

  return Array.from( counts.entries())
    .map(([ entityId, count ]) => ({ entityId, count }))
    .sort(( a, b ) => b.count - a.count )
    .slice( 0, MAX_LEAVES_PER_GROUP );
};

/**
 * Shape of a publication enrichment lookup result (bounded, ≤25 ids).
 */
type PublicationInfo = {
  id: string;
  title: string;
  sku?: string;
  /** Owning integration id (`[connectorId]_[integrationUserId]`) or null. */
  channelId: string | null;
};

/**
 * Bounded enrichment: fetch the ≤25 rule-owning publications by id to obtain
 * their display title, sku, and owning channel id (so the publication leaf's
 * `channel` ancestor step is correct and the leaf nests under its channel).
 * Failures degrade to id-only labels (best-effort — never blocks the tree).
 */
const fetchPublicationInfos = async (
  backend: ClientLibraryType,
  publicationIds: string[]
): Promise<Map<string, PublicationInfo>> => {
  const infos = new Map<string, PublicationInfo>();

  await Promise.all(
    publicationIds.map( async ( id ) => {
      try {
        const pub = ( await backend.queries.ItemsPublishingPublicationsById( id )) as Record<
          string,
          unknown
        >;
        const connectorId = pub.connectorId as string | undefined;
        const integrationUserId = pub.integrationUserId as string | undefined;
        const channelId =
          connectorId && integrationUserId ? `${connectorId}_${integrationUserId}` : null;
        infos.set( id, {
          id,
          title: resolveLabel( pub.title, id ),
          sku: ( pub.publicationSku as string | undefined ) ?? undefined,
          channelId
        });
      } catch {
        infos.set( id, { id, title: id, channelId: null });
      }
    })
  );

  return infos;
};

/**
 * Bounded enrichment: fetch the ≤25 rule-owning items by id to obtain their
 * display name + sku. Failures degrade to id-only labels.
 */
const fetchItemInfos = async (
  backend: ClientLibraryType,
  itemIds: string[]
): Promise<Map<string, { id: string; label: string; sku?: string }>> => {
  const infos = new Map<string, { id: string; label: string; sku?: string }>();

  await Promise.all(
    itemIds.map( async ( id ) => {
      try {
        const item = ( await backend.queries.ItemsPublishingInventoryById( id )) as Record<
          string,
          unknown
        >;
        infos.set( id, {
          id,
          label: resolveLabel( item.name ?? item.title, id ),
          sku: ( item.sku as string | undefined ) ?? undefined
        });
      } catch {
        infos.set( id, { id, label: id });
      }
    })
  );

  return infos;
};

// =============================================================================
// TREE BUILDER
// =============================================================================

/**
 * Build a single channel node (kind 'channel') with a publication-hierarchy
 * matching path. Publication leaves owned by this channel are attached.
 */
const buildChannelNode = (
  channel: SystemIntegrationsReadmodelType,
  accountId: string,
  logo: string | undefined,
  publicationLeaves: ScopeNode[]
): ScopeNode => {
  const channelId = channel.id;
  // channel + integrationUserId both carry the integration id (back-compat).
  const ancestors: ScopeAncestor[] = [
    { level: 'account', entityId: accountId },
    { level: 'channel', entityId: channelId },
    { level: 'integrationUserId', entityId: channelId }
  ];

  return {
    id: channelId,
    kind: 'channel',
    label: resolveLabel( channel.label, channel.connectorId || channelId ),
    hierarchy: 'publication',
    ancestors,
    sub: channel.channelUserName || undefined,
    logo,
    children: publicationLeaves
  };
};

/**
 * Build a publication leaf (kind 'publication', hierarchy 'publication').
 * Ancestors: [ account, channel, publication ]. When the owning channel is
 * unknown (enrichment failed / no link), the channel step is omitted — the
 * resolver then matches publication-own + account-inherited rules only (a
 * documented best-effort gap rather than an incorrect channel match).
 */
const buildPublicationLeaf = (
  publicationId: string,
  accountId: string,
  info: PublicationInfo | undefined
): ScopeNode => {
  const ancestors: ScopeAncestor[] = [{ level: 'account', entityId: accountId }];
  if ( info?.channelId ) {
    ancestors.push({ level: 'channel', entityId: info.channelId });
  }
  ancestors.push({ level: 'publication', entityId: publicationId });

  return {
    id: publicationId,
    kind: 'publication',
    label: info?.title ?? publicationId,
    hierarchy: 'publication',
    ancestors,
    sub: info?.sku,
    children: []
  };
};

/**
 * Build an item leaf (kind 'item', hierarchy 'item').
 * Ancestors: [ account, item ]. The warehouse step is intentionally omitted —
 * the rules data carries no item→warehouse association, so warehouse-level
 * inheritance for an item leaf is a documented v1 gap.
 */
const buildItemLeaf = (
  itemId: string,
  accountId: string,
  info: { id: string; label: string; sku?: string } | undefined
): ScopeNode => ({
  id: itemId,
  kind: 'item',
  label: info?.label ?? itemId,
  hierarchy: 'item',
  ancestors: [
    { level: 'account', entityId: accountId },
    { level: 'item', entityId: itemId }
  ],
  sub: info?.sku,
  children: []
});

/**
 * Assemble a FLAT scope tree: `Cuenta`, `Canales`, and `Publicaciones` each sit
 * at the first level (not nested account → channels → publications). Visual
 * nesting is decoupled from inheritance — every node still carries its full
 * `ancestors` path, so selecting a channel/publication shows the inherited
 * parent rules in its own list regardless of where it sits in the panel.
 *
 * - Cuenta        : a single selectable account-level node.
 * - Canales       : ALL connected channels (selectable even with 0 rules).
 * - Publicaciones : sparse — only publications that OWN a rule (no lazy pinning).
 * - Ítems         : rendered only when some rule is item-scoped (sparse).
 *
 * Returns a synthetic invisible container root; the ScopeTree renders its
 * children as the first level.
 */
const buildScopeTree = (
  rules: RulesReadmodelType[],
  channels: SystemIntegrationsReadmodelType[],
  publicationInfos: Map<string, PublicationInfo>,
  publicationIds: string[],
  itemInfos: Map<string, { id: string; label: string; sku?: string }>,
  itemIds: string[]
): ScopeNode => {
  const accountId = deriveAccountId( rules );

  // Cuenta — first-level selectable leaf (account-scoped rules only).
  const accountNode: ScopeNode = {
    id: ACCOUNT_NODE_ID,
    kind: 'account',
    label: 'Cuenta',
    ancestors: [{ level: 'account', entityId: accountId }],
    children: []
  };

  // Canales — ALL connected channels, flat (publications are NOT nested here).
  // The logo key is the connectorId ('mercadolibre', …); the ScopeTree resolves
  // it to an image via the shared integrations connectors map.
  const channelNodes: ScopeNode[] = channels.map(( channel ) =>
    buildChannelNode( channel, accountId, channel.connectorId, []));
  const canalesGroup: ScopeNode = {
    id: 'group-channels',
    kind: 'group',
    label: 'Canales',
    ancestors: [],
    children: channelNodes
  };

  const firstLevel: ScopeNode[] = [ accountNode, canalesGroup ];

  // Publicaciones — flat list of sparse publication leaves (those that own a
  // rule). Each leaf keeps its [account, channel, publication] ancestors so the
  // channel + account rules are inherited into its list.
  const publicationLeaves: ScopeNode[] = publicationIds.map(( pubId ) =>
    buildPublicationLeaf( pubId, accountId, publicationInfos.get( pubId )));
  if ( publicationLeaves.length > 0 ) {
    firstLevel.push({
      id: 'group-publications',
      kind: 'group',
      label: 'Publicaciones',
      ancestors: [],
      children: publicationLeaves
    });
  }

  // Ítems — only when some rule is item-scoped (sparse; no lazy pinning).
  const itemLeaves: ScopeNode[] = itemIds.map(( itemId ) =>
    buildItemLeaf( itemId, accountId, itemInfos.get( itemId )));
  if ( itemLeaves.length > 0 ) {
    firstLevel.push({
      id: 'group-items',
      kind: 'group',
      label: 'Ítems',
      ancestors: [],
      children: itemLeaves
    });
  }

  // Synthetic invisible container — the ScopeTree renders its children at level 0.
  return {
    id: '__root__',
    kind: 'group',
    label: '',
    ancestors: [],
    children: firstLevel
  };
};

// =============================================================================
// POLICY
// =============================================================================

export const createScopeReferenceDataPolicy = (
  backend: ClientLibraryType
): Policy<RulesCatalogState, RulesCatalogCommand> => ({
  name: 'automation-rules-catalog-scope-reference-data',

  init: ({ dispatch }) => {
    ( async () => {
      dispatch(
        { type: 'automation/rulesCatalog/loading/setRulesLoading', payload: { isLoading: true }},
        { source: 'api' }
      );
      dispatch(
        { type: 'automation/rulesCatalog/loading/setRefDataLoading', payload: { isLoading: true }},
        { source: 'api' }
      );

      try {
        // 1. Rules (incl. bypass) + connected channels in parallel.
        const [ rules, integrations ] = await Promise.all([
          backend.queries.Rules({ filter: RULES_FILTER, select: RULES_SELECT }),
          backend.queries.ExternalSystemsIntegrations()
        ]);

        const safeRules = rules || [];

        dispatch(
          { type: 'automation/rulesCatalog/data/setRules', payload: { rules: safeRules }},
          { source: 'api' }
        );

        // Sales-channel integrations only. The readmodel stamps the connector's
        // static kind under `connectorKind` (NOT `type`) — filtering on a
        // non-existent `type` field silently yields zero channels.
        const channels = ( integrations || []).filter(
          ( i ) => ( i as SystemIntegrationsReadmodelType ).connectorKind === 'salesChannel'
        ) as SystemIntegrationsReadmodelType[];

        // 2. Sparse leaves (Pattern A): top-N rule-owning entities per group.
        const topPublications = topEntityIdsForLevel( safeRules, 'publication' );
        const topItems = topEntityIdsForLevel( safeRules, 'item' );
        const publicationIds = topPublications.map(( e ) => e.entityId );
        const itemIds = topItems.map(( e ) => e.entityId );

        // 3. Bounded name/channel enrichment over just those ≤25 ids per group.
        const [ publicationInfos, itemInfos ] = await Promise.all([
          fetchPublicationInfos( backend, publicationIds ),
          fetchItemInfos( backend, itemIds )
        ]);

        const scopeTree = buildScopeTree(
          safeRules,
          channels,
          publicationInfos,
          publicationIds,
          itemInfos,
          itemIds
        );

        dispatch(
          { type: 'automation/rulesCatalog/data/setScopeTree', payload: { scopeTree }},
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[automation-rules-catalog] Scope reference data error:', error );
        throw error;
      } finally {
        dispatch(
          { type: 'automation/rulesCatalog/loading/setRulesLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
        dispatch(
          { type: 'automation/rulesCatalog/loading/setRefDataLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
      }
    })();

    // One-time fetch — no cleanup needed.
    return undefined;
  }
});
